/**
 * SectionBadge Component
 * ======================
 * Badge component for section labels/headers.
 */

import React from 'react';

/**
 * SectionBadge Component
 * Small label/badge for section headers.
 * 
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Badge content
 * @param {string} [props.variant='default'] - Badge variant
 * @param {string} [props.size='md'] - Badge size
 * @param {React.ReactNode} [props.icon] - Optional icon
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} SectionBadge component
 */
export function SectionBadge({
  children,
  variant = 'default',
  size = 'md',
  icon,
  className = '',
}) {
  const variants = {
    default: 'bg-dme-orange-50 text-dme-orange border-dme-orange-200',
    blue: 'bg-dme-blue-50 text-dme-blue border-dme-blue-200',
    green: 'bg-dme-green-50 text-dme-green border-dme-green-200',
    dark: 'bg-dme-blue-800 text-white border-dme-blue-700',
    outline: 'bg-transparent text-dme-gray-600 border-dme-gray-300',
    'outline-white': 'bg-transparent text-white border-white/30',
  };
  
  const sizes = {
    sm: 'px-3 py-1 text-xs',
    md: 'px-4 py-1.5 text-sm',
    lg: 'px-5 py-2 text-base',
  };
  
  const classes = `
    inline-flex items-center gap-2
    font-semibold rounded-full border
    uppercase tracking-wider
    ${variants[variant] || variants.default}
    ${sizes[size] || sizes.md}
    ${className}
  `;
  
  return (
    <span className={classes}>
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </span>
  );
}

/**
 * SectionHeader Component
 * Combined badge + heading for section headers.
 * 
 * @param {object} props - Component props
 * @param {string} [props.badge] - Badge text
 * @param {string} props.title - Section title
 * @param {string} [props.subtitle] - Section subtitle/description
 * @param {string} [props.align='center'] - Text alignment
 * @param {string} [props.badgeVariant='default'] - Badge variant
 * @param {boolean} [props.light=false] - Light theme (for dark backgrounds)
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} SectionHeader component
 */
export function SectionHeader({
  badge,
  title,
  subtitle,
  align = 'center',
  badgeVariant = 'default',
  light = false,
  className = '',
}) {
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };
  
  const titleColor = light ? 'text-white' : 'text-dme-blue-800';
  const subtitleColor = light ? 'text-white/70' : 'text-dme-gray-600';
  
  return (
    <div className={`${alignClasses[align] || alignClasses.center} ${className}`}>
      {badge && (
        <SectionBadge 
          variant={light ? 'outline-white' : badgeVariant}
          className="mb-4"
        >
          {badge}
        </SectionBadge>
      )}
      
      <h2 className={`font-heading font-bold text-3xl lg:text-4xl xl:text-5xl ${titleColor} mb-4`}>
        {title}
      </h2>
      
      {subtitle && (
        <p className={`text-lg lg:text-xl max-w-3xl ${align === 'center' ? 'mx-auto' : ''} ${subtitleColor}`}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

export default SectionBadge;
